/**
 * Enterprise Dashboard
 *
 * Main dashboard for enterprise administrators showing:
 * - Enterprise profile overview
 * - Channel Partners management
 * - Organizations view (read-only)
 * - User overview
 * - Analytics summary
 *
 * IMPORTANT: Enterprise users cannot access clinical data (PHI) - HIPAA compliance
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2,
  Users,
  TrendingUp,
  Plus,
  Search,
  Edit2,
  Trash2,
  Eye,
  AlertCircle,
  CheckCircle,
  Clock,
  XCircle,
  Briefcase,
  CreditCard
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { apiService } from '@/services/api';
import { toast } from 'react-hot-toast';
import { DataTable, ColumnDefinition, ActionDefinition, SortState } from '@/components/shared/DataTable';

interface EnterpriseProfile {
  id: string;
  name: string;
  slug: string;
  type: string;
  status: string;
  contact: {
    primaryName: string;
    primaryEmail: string;
    primaryPhone?: string;
  };
  quotas: {
    maxChannelPartners: number;
    maxOrganizations: number;
    maxUsersPerOrg: number;
    maxStorageGB: number;
  };
  features: {
    whiteLabeling: boolean;
    customBranding: boolean;
    apiAccess: boolean;
    advancedAnalytics: boolean;
    multiRegion: boolean;
  };
  stats: {
    channelPartnerCount: number;
    organizationCount: number;
    userCount: number;
  };
}

interface ChannelPartner {
  id: string;
  name: string;
  slug: string;
  type: string;
  status: string;
  contact: {
    primaryName: string;
    primaryEmail: string;
  };
  stats: {
    organizationCount: number;
    userCount: number;
  };
  createdAt: string;
}

interface EnterpriseAnalytics {
  overview: {
    totalChannelPartners: number;
    activeChannelPartners: number;
    totalOrganizations: number;
    activeOrganizations: number;
    totalUsers: number;
    totalEncounters: number;
  };
  recentActivity: {
    newPartnersLast30Days: number;
    newOrganizationsLast30Days: number;
    newUsersLast30Days: number;
  };
  partnersByStatus: Record<string, number>;
}

const EnterpriseDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [profile, setProfile] = useState<EnterpriseProfile | null>(null);
  const [channelPartners, setChannelPartners] = useState<ChannelPartner[]>([]);
  const [analytics, setAnalytics] = useState<EnterpriseAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortState, setSortState] = useState<SortState>({ column: 'name', direction: 'asc' });
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const isAdmin = user?.role === 'enterprise_admin';

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      interface ProfileResponse { success: boolean; data: EnterpriseProfile }
      interface PartnersResponse { success: boolean; data: { channelPartners: ChannelPartner[] } }
      interface AnalyticsResponse { success: boolean; data: EnterpriseAnalytics }

      const [profileRes, partnersRes, analyticsRes] = await Promise.all([
        apiService.get<ProfileResponse>('/v1/enterprise/profile'),
        apiService.get<PartnersResponse>('/v1/enterprise/channel-partners'),
        apiService.get<AnalyticsResponse>('/v1/enterprise/analytics')
      ]);

      setProfile(profileRes.data);
      setChannelPartners(partnersRes.data?.channelPartners || []);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      console.error('Failed to load enterprise data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateChannelPartner = () => {
    navigate('/enterprise/partners/new');
  };

  const handleViewChannelPartner = (partnerId: string) => {
    navigate(`/enterprise/partners/${partnerId}`);
  };

  const handleDeleteChannelPartner = async (partnerId: string) => {
    if (!confirm('Are you sure you want to delete this channel partner? This will affect all associated organizations.')) {
      return;
    }

    try {
      await apiService.delete(`/v1/enterprise/channel-partners/${partnerId}`);
      toast.success('Channel partner deleted successfully');
      loadDashboardData();
    } catch (error) {
      console.error('Failed to delete channel partner:', error);
      toast.error('Failed to delete channel partner');
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'suspended':
        return <AlertCircle className="h-4 w-4 text-yellow-500" />;
      case 'cancelled':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusBadgeClasses = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'suspended':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredPartners = channelPartners.filter(partner =>
    partner.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    partner.slug.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Sorting logic
  const sortedPartners = useMemo(() => {
    if (!sortState.column || !sortState.direction) return filteredPartners;

    const getValue = (obj: ChannelPartner, path: string): string | number | null => {
      const parts = path.split('.');
      let value: unknown = obj;
      for (const part of parts) {
        value = (value as Record<string, unknown>)?.[part];
      }
      return value as string | number | null;
    };

    return [...filteredPartners].sort((a, b) => {
      const aVal = getValue(a, sortState.column!);
      const bVal = getValue(b, sortState.column!);
      if (aVal == null) return sortState.direction === 'asc' ? 1 : -1;
      if (bVal == null) return sortState.direction === 'asc' ? -1 : 1;
      const cmp = typeof aVal === 'string'
        ? aVal.localeCompare(bVal as string)
        : Number(aVal) - Number(bVal);
      return sortState.direction === 'asc' ? cmp : -cmp;
    });
  }, [filteredPartners, sortState]);

  // Column definitions
  const columns: ColumnDefinition<ChannelPartner>[] = useMemo(() => [
    {
      id: 'name',
      header: 'Channel Partner',
      accessor: (row) => (
        <div>
          <div className="text-sm font-medium text-gray-900">{row.name}</div>
          <div className="text-xs text-gray-500">{row.slug}</div>
        </div>
      ),
      sortable: true,
      sortKey: 'name'
    },
    {
      id: 'type',
      header: 'Type',
      accessor: (row) => (
        <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-700 capitalize">
          {row.type}
        </span>
      ),
      sortable: true,
      sortKey: 'type',
      hideOnMobile: true
    },
    {
      id: 'status',
      header: 'Status',
      accessor: (row) => (
        <div className="flex items-center gap-2">
          {getStatusIcon(row.status)}
          <span className="text-sm text-gray-700 capitalize">{row.status}</span>
        </div>
      ),
      sortable: true,
      sortKey: 'status'
    },
    {
      id: 'organizations',
      header: 'Organizations',
      accessor: (row) => (
        <span className="text-sm text-gray-700">{row.stats?.organizationCount || 0}</span>
      ),
      sortable: true,
      sortKey: 'stats.organizationCount',
      hideOnTablet: true
    },
    {
      id: 'users',
      header: 'Users',
      accessor: (row) => (
        <span className="text-sm text-gray-700">{row.stats?.userCount || 0}</span>
      ),
      sortable: true,
      sortKey: 'stats.userCount',
      hideOnTablet: true
    },
    {
      id: 'createdAt',
      header: 'Created',
      accessor: (row) => (
        <span className="text-sm text-gray-500">
          {new Date(row.createdAt).toLocaleDateString()}
        </span>
      ),
      sortable: true,
      sortKey: 'createdAt',
      hideOnMobile: true
    }
  ], []);

  // Action definitions (only shown if admin)
  const actions: ActionDefinition<ChannelPartner>[] = useMemo(() => {
    if (!isAdmin) return [];
    return [
      {
        id: 'view',
        label: 'View',
        icon: <Eye className="h-4 w-4" />,
        onClick: (row) => handleViewChannelPartner(row.id)
      },
      {
        id: 'edit',
        label: 'Edit',
        icon: <Edit2 className="h-4 w-4" />,
        onClick: (row) => navigate(`/enterprise/partners/${row.id}/edit`)
      },
      {
        id: 'delete',
        label: 'Delete',
        icon: <Trash2 className="h-4 w-4" />,
        onClick: (row) => handleDeleteChannelPartner(row.id),
        variant: 'danger'
      }
    ];
  }, [isAdmin, navigate]);

  // Render expanded row content
  const renderExpanded = useCallback((partner: ChannelPartner) => (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
      <div>
        <p className="text-gray-500">Contact</p>
        <p className="font-medium text-gray-900">{partner.contact?.primaryName || 'N/A'}</p>
        <p className="text-gray-600">{partner.contact?.primaryEmail || 'N/A'}</p>
      </div>
      <div>
        <p className="text-gray-500">Statistics</p>
        <p className="text-gray-700">
          {partner.stats?.organizationCount || 0} organizations,{' '}
          {partner.stats?.userCount || 0} users
        </p>
      </div>
    </div>
  ), []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            {profile?.name || 'Enterprise Portal'}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage your channel partners and view organization activity
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className={`px-3 py-1 text-xs font-medium rounded-full ${getStatusBadgeClasses(profile?.status || 'active')}`}>
            {profile?.status?.toUpperCase()}
          </span>
          <span className="px-3 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-800">
            {profile?.type?.toUpperCase()}
          </span>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Channel Partners</p>
              <p className="text-2xl font-semibold text-gray-900 mt-1">
                {analytics?.overview.totalChannelPartners || 0}
              </p>
            </div>
            <div className="h-12 w-12 rounded-full bg-amber-100 flex items-center justify-center">
              <Briefcase className="h-6 w-6 text-amber-600" />
            </div>
          </div>
          {profile?.quotas.maxChannelPartners !== -1 && (
            <div className="mt-4">
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>Quota Used</span>
                <span>{profile?.stats.channelPartnerCount}/{profile?.quotas.maxChannelPartners}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-amber-600 h-2 rounded-full"
                  style={{
                    width: `${Math.min(100, (profile?.stats.channelPartnerCount || 0) / (profile?.quotas.maxChannelPartners || 1) * 100)}%`
                  }}
                />
              </div>
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Organizations</p>
              <p className="text-2xl font-semibold text-gray-900 mt-1">
                {analytics?.overview.totalOrganizations || 0}
              </p>
            </div>
            <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
              <Building2 className="h-6 w-6 text-blue-600" />
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-4">
            {analytics?.overview.activeOrganizations || 0} active
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Users</p>
              <p className="text-2xl font-semibold text-gray-900 mt-1">
                {analytics?.overview.totalUsers || 0}
              </p>
            </div>
            <div className="h-12 w-12 rounded-full bg-violet-100 flex items-center justify-center">
              <Users className="h-6 w-6 text-violet-600" />
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-4">
            +{analytics?.recentActivity.newUsersLast30Days || 0} this month
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Growth</p>
              <p className="text-2xl font-semibold text-gray-900 mt-1">
                +{analytics?.recentActivity.newOrganizationsLast30Days || 0}
              </p>
            </div>
            <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
              <TrendingUp className="h-6 w-6 text-green-600" />
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-4">
            New orgs this month
          </p>
        </div>
      </div>

      {/* Channel Partners Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Channel Partners</h2>
            <div className="flex items-center gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search partners..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                />
              </div>
              {isAdmin && (profile?.quotas.maxChannelPartners === -1 ||
                (profile?.stats.channelPartnerCount || 0) < (profile?.quotas.maxChannelPartners || 0)) && (
                <button
                  onClick={handleCreateChannelPartner}
                  className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  Add Channel Partner
                </button>
              )}
            </div>
          </div>
        </div>

        <DataTable
          data={sortedPartners}
          columns={columns}
          actions={actions}
          getRowId={(row) => row.id}
          loading={false}
          emptyMessage={searchQuery ? 'No channel partners found. Try adjusting your search.' : 'No channel partners found'}
          emptyIcon={<Briefcase className="h-12 w-12 text-gray-300" />}
          sortable
          sortState={sortState}
          onSortChange={setSortState}
          expandable
          expandedIds={expandedIds}
          onExpansionChange={setExpandedIds}
          renderExpanded={renderExpanded}
        />
      </div>

      {/* Quick Links */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <button
          onClick={() => navigate('/enterprise/organizations')}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow text-left"
        >
          <Building2 className="h-8 w-8 text-blue-600 mb-4" />
          <h3 className="text-lg font-medium text-gray-900">View Organizations</h3>
          <p className="text-sm text-gray-500 mt-1">
            See all organizations across your channel partners
          </p>
        </button>

        <button
          onClick={() => navigate('/enterprise/users')}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow text-left"
        >
          <Users className="h-8 w-8 text-violet-600 mb-4" />
          <h3 className="text-lg font-medium text-gray-900">View Users</h3>
          <p className="text-sm text-gray-500 mt-1">
            Browse all users in your enterprise hierarchy
          </p>
        </button>

        <button
          onClick={() => navigate('/enterprise/usage')}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow text-left"
        >
          <CreditCard className="h-8 w-8 text-green-600 mb-4" />
          <h3 className="text-lg font-medium text-gray-900">Usage & Billing</h3>
          <p className="text-sm text-gray-500 mt-1">
            Monitor usage metrics and billing information
          </p>
        </button>
      </div>

      {/* HIPAA Notice */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
          <div>
            <h3 className="text-sm font-medium text-amber-800">HIPAA Compliance Notice</h3>
            <p className="text-sm text-amber-700 mt-1">
              As an enterprise administrator, you can manage channel partners and view organization metrics
              but cannot access clinical data or Protected Health Information (PHI). All clinical operations
              must be performed by organization-level users with appropriate clinical permissions.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnterpriseDashboard;
